import { Siren } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { canToggleEmergency } from "@/lib/dateFmt";

/**
 * Phase 4 — Emergency Requisition toggle (used on all 7 New*.jsx forms).
 *
 * Props:
 *   isEmergency        : boolean
 *   onToggle(bool)     : called with the new value when the switch flips
 *   emergencyReason    : string
 *   onReasonChange(v)  : called on textarea change
 *   showError          : boolean — if true and toggle is on but reason<10 chars, red-outline
 *
 * Renders a compact sheet with a switch pill + conditional textarea. Backend
 * mirrors the fields onto the requisition doc and prefixes `[EMERGENCY]` to
 * every stage/reminder email subject when the flag is on.
 */
export default function EmergencyToggle({
  isEmergency,
  onToggle,
  emergencyReason,
  onReasonChange,
  showError = false,
}) {
  const { user } = useAuth();
  const canToggle = canToggleEmergency(user);
  const reasonLen = (emergencyReason || "").trim().length;
  const invalid = isEmergency && reasonLen < 10;

  // Phase 8 — Emergency flag is now VIEW-ONLY for non-authorised users.
  // Applicants / HODs / Admins etc. can see the chip if it's on but can
  // no longer toggle it. Only CAO + Jahnavi may flip it (backend gate too).
  if (!canToggle) {
    if (!isEmergency) return null;
    return (
      <section className="sheet p-4 mt-4 border-2 border-[#b3261e]/60 bg-[#fdf3f1]"
                data-testid="emergency-view-only-banner">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-full flex items-center justify-center bg-[#b3261e] text-white">
            <Siren size={18} />
          </div>
          <div>
            <span className="inline-flex items-center gap-1 rounded-full bg-[#b3261e] text-white px-2 py-0.5 text-[11px] font-semibold uppercase tracking-widest">
              EMERGENCY
            </span>
            <div className="text-xs text-[#4a524c] mt-1">
              This requisition has been marked emergency by an authorised officer.
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section
      className={`sheet p-5 sm:p-6 mt-4 border-2 transition-colors ${
        isEmergency ? "border-[#b3261e]/60 bg-[#fdf3f1]" : "border-transparent"
      }`}
      data-testid="emergency-toggle-section"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3">
          <div
            className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${
              isEmergency ? "bg-[#b3261e] text-white" : "bg-[#faf5eb] text-[#b3261e]"
            }`}
          >
            <Siren size={20} />
          </div>
          <div>
            <div className="font-serif text-lg font-semibold text-[#1f2320]">
              Emergency Requisition
            </div>
            <p className="text-sm text-[#4a524c] mt-0.5 max-w-md">
              Flip this only when the requirement is time-critical and must be
              expedited by approvers. All emails will be prefixed with{" "}
              <span className="font-mono text-[11px] bg-[#faf5eb] px-1.5 py-0.5 rounded text-[#7a1f2b]">
                [EMERGENCY]
              </span>{" "}
              and the dashboards will surface a red pill.
            </p>
          </div>
        </div>

        <button
          type="button"
          role="switch"
          aria-checked={isEmergency}
          onClick={() => onToggle(!isEmergency)}
          className={`relative inline-flex h-7 w-14 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-[#7a1f2b] focus:ring-offset-2 ${
            isEmergency ? "bg-[#b3261e]" : "bg-[#cbbba0]"
          }`}
          data-testid="emergency-toggle-switch"
        >
          <span
            className={`pointer-events-none inline-block h-6 w-6 transform rounded-full bg-white shadow-lg transition-transform ${
              isEmergency ? "translate-x-7" : "translate-x-0"
            }`}
          />
        </button>
      </div>

      {isEmergency && (
        <div className="mt-4">
          <label className="block">
            <span className="block text-sm font-medium text-[#1f2320] mb-1.5">
              Emergency Reason <span className="text-[#b3261e]">*</span>
              <span className="ml-2 text-[10px] uppercase tracking-widest text-[#6e7770]">
                min 10 characters
              </span>
            </span>
            <textarea
              className={`input-vgws min-h-[90px] ${
                showError && invalid ? "!border-[#b3261e] ring-1 ring-[#b3261e]/30" : ""
              }`}
              rows={3}
              value={emergencyReason || ""}
              onChange={(e) => onReasonChange(e.target.value.slice(0, 500))}
              placeholder="Describe why this requisition must be expedited (min 10 characters)..."
              data-testid="emergency-reason-textarea"
            />
            <div className="mt-1 flex items-center justify-between text-xs">
              <span
                className={
                  invalid ? "text-[#b3261e]" : reasonLen >= 10 ? "text-[#2d6b6b]" : "text-[#6e7770]"
                }
                data-testid="emergency-reason-hint"
              >
                {invalid
                  ? "Emergency reason is mandatory (minimum 10 characters)."
                  : reasonLen >= 10
                  ? "Looks good."
                  : "Explain why this cannot follow the standard timeline."}
              </span>
              <span className="font-mono text-[#6e7770]">{reasonLen} / 500</span>
            </div>
          </label>
        </div>
      )}
    </section>
  );
}
